import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message } from 'antd';
import { CopyOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import IntlMessages from 'util/IntlMessages';
import DataService from '../Services/DataService';
import PinCode from 'react-pin-code-input-component';
import logoWhite from 'assets/images/logo-white.png';

const SignIn = (props) => {
  const token = useSelector(({ auth }) => auth.token);
  // "password" → "setup" (first sign-in: scan the QR code) or "verify" (every
  // later sign-in). No page opens until one of those two steps succeeds: the
  // API only issues the session token after the Google Authenticator code.
  const [step, setStep] = useState('password');
  const [challengeToken, setChallengeToken] = useState(null);
  const [setup, setSetup] = useState({ qrcode: '', manualKey: '' });
  const [code, setCode] = useState('');
  const [pinKey, setPinKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const errorText = (err, fallback) => err?.response?.data?.message || fallback;

  const clearPin = () => {
    setCode('');
    setPinKey((k) => k + 1);
  };

  const restart = (notice) => {
    setStep('password');
    setChallengeToken(null);
    setSetup({ qrcode: '', manualKey: '' });
    clearPin();
    if (notice) message.warning(notice);
  };

  const onFinishFailed = () => {};

  const onFinish = (values) => {
    setSubmitting(true);
    DataService.doLogin({ email: values.email, password: values.password })
      .then((res) => {
        const result = res?.data?.result;
        if (result === false) {
          message.error('Please make sure you are connected via VPN');
          return undefined;
        }
        if (!result?.challengeToken) {
          message.error('Login failed. Please check your credentials.');
          return undefined;
        }
        setChallengeToken(result.challengeToken);
        clearPin();
        if (result.next === 'SETUP_2FA') {
          setStep('setup');
          return DataService.startLoginMfaSetup(result.challengeToken)
            .then((r) => setSetup(r.data.result));
        }
        setStep('verify');
        return undefined;
      })
      .catch((err) => {
        message.error(errorText(err, 'Login failed. Please check your credentials.'));
      })
      .finally(() => setSubmitting(false));
  };

  // Keep what the rest of the app reads from localStorage, then open the app.
  const finishSignIn = (session) => {
    localStorage.setItem('isGAuthEnabled', session.isGAuthEnabled);
    localStorage.setItem('userId', session.id);
    localStorage.setItem('name', session.name);
    localStorage.setItem('role', session.role);
    localStorage.setItem('memberSince', session.createdAt || '');
    localStorage.setItem('profilePictureUrl', session.profilePictureUrl || '');
    return DataService.getUserAllPolicies(session.id, session.accessToken).then((response) => {
      const userPolicies = (response?.data?.result || []).map((policy) => policy.name);
      localStorage.setItem('userPolicies', JSON.stringify(userPolicies));
      localStorage.setItem('adminToken', session.accessToken);
      window.location.href = '/';
    });
  };

  const submitCode = () => {
    if (!/^\d{6}$/.test(code)) {
      message.error('Please enter all 6 digits');
      return;
    }
    setSubmitting(true);
    const request = step === 'setup'
      ? DataService.completeLoginMfaSetup(challengeToken, code)
      : DataService.verifyLoginMfa(challengeToken, code);
    request
      .then((res) => finishSignIn(res.data.result))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          restart('Your sign-in timed out. Enter your password again.');
        } else if (status === 429) {
          restart(errorText(err, 'Too many wrong codes. Try again later.'));
        } else {
          message.error(errorText(err, 'That code did not work. Enter the current code from the app.'));
          clearPin();
        }
      })
      .finally(() => setSubmitting(false));
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(setup.manualKey)
      .then(() => message.success('Key copied'))
      .catch(() => message.info('Select the key and copy it'));
  };

  useEffect(() => {
    if (token !== null) props.history.push('/');
  }, [token, props.history]);

  const heading = {
    password: { title: 'Welcome back', subtitle: 'Sign in to your merchant account' },
    setup: { title: 'Set up two-factor authentication', subtitle: 'Required before you can use the portal' },
    verify: { title: 'Enter your authentication code', subtitle: 'From Google Authenticator on your phone' },
  }[step];

  const pinInput = (
    <div style={{ marginBottom: 24 }}>
      <PinCode
        key={pinKey}
        className='fex-pincode'
        type='text'
        fields={6}
        onChange={(value) => setCode(String(value))}
      />
    </div>
  );

  return (
    // Outer wrap — .gx-app-login-wrap already gets the dark BG via custom.less
    <div className='gx-app-login-wrap'>
      <div className='gx-app-login-container'>
        <div className='gx-app-login-main-content' style={card.wrapper}>

          {/* ── Left brand panel ─────────────────────────────── */}
          <div style={card.brand}>
            {/* Background tint */}
            <div style={card.brandBg} />
            <div style={card.brandContent}>
              <img
                src={logoWhite}
                alt='Fasset'
                style={card.logo}
              />
              <h2 style={card.brandTitle}>P2P Merchant Portal</h2>
              <p style={card.brandSub}>
                Manage your P2P trading operations — real-time transactions, merchant controls, and performance insights.
              </p>
              <ul style={card.featureList}>
                {[
                  'Real-time transaction management',
                  'Merchant account controls',
                  'Secure 2-factor authentication',
                ].map((f) => (
                  <li key={f} style={card.featureItem}>
                    <span style={card.featureDot} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Right form panel ─────────────────────────────── */}
          <div className='gx-app-login-content' style={card.form}>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 22, fontWeight: 600, color: '#122117', marginBottom: 4 }}>
                {heading.title}
              </h2>
              <p style={{ color: '#8c8c8c', fontSize: 13, margin: 0 }}>
                {heading.subtitle}
              </p>
            </div>

            {step === 'password' && (
              <Form
                initialValues={{ remember: true }}
                name='basic'
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout='vertical'
                className='gx-signin-form gx-form-row0'
                size='large'
              >
                <Form.Item
                  name='email'
                  label={<span style={{ fontSize: 13, fontWeight: 500 }}>Email Address</span>}
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder='you@example.com'
                    autoComplete='email'
                  />
                </Form.Item>

                <Form.Item
                  name='password'
                  label={<span style={{ fontSize: 13, fontWeight: 500 }}>Password</span>}
                  rules={[{ required: true, message: 'Please enter your password' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder='Enter your password'
                    autoComplete='current-password'
                  />
                </Form.Item>

                <Form.Item style={{ marginTop: 8 }}>
                  <Button
                    type='primary'
                    htmlType='submit'
                    block
                    loading={submitting}
                    style={{ height: 44, fontWeight: 600, fontSize: 15 }}
                  >
                    {submitting ? 'Signing in…' : <IntlMessages id='app.userAuth.signIn' />}
                  </Button>
                </Form.Item>
              </Form>
            )}

            {step === 'setup' && (
              <div>
                <p style={twoFa.label}><b>1.</b> Scan this QR code with <b>Google Authenticator</b>.</p>
                <div style={twoFa.qr}>
                  {setup.qrcode
                    ? <img src={setup.qrcode} alt='QR code for Google Authenticator' style={twoFa.qrImg} />
                    : <div style={twoFa.qrPlaceholder} aria-label='Loading QR code' />}
                </div>
                <p style={twoFa.label}>Can't scan it? Enter this key in the app instead.</p>
                <div style={twoFa.key}>
                  <code style={twoFa.keyCode}>
                    {setup.manualKey ? setup.manualKey.match(/.{1,4}/g).join(' ') : '…'}
                  </code>
                  <Button
                    type='text'
                    size='small'
                    icon={<CopyOutlined />}
                    onClick={copyKey}
                    aria-label='Copy key'
                    disabled={!setup.manualKey}
                  />
                </div>
                <p style={twoFa.label}><b>2.</b> Enter the 6-digit code the app shows.</p>
                {pinInput}
                <Button
                  type='primary'
                  block
                  size='large'
                  onClick={submitCode}
                  loading={submitting}
                  disabled={!setup.manualKey}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Turn on and sign in
                </Button>
                <Button type='link' block onClick={() => restart()} style={twoFa.link}>
                  Use a different account
                </Button>
              </div>
            )}

            {step === 'verify' && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={twoFa.iconWrap}>
                    <SafetyOutlined style={{ fontSize: 28, color: '#3daa4e' }} />
                  </div>
                </div>
                {pinInput}
                <Button
                  type='primary'
                  block
                  size='large'
                  onClick={submitCode}
                  loading={submitting}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Verify and sign in
                </Button>
                <p style={twoFa.help}>
                  Lost your phone? Ask a superadmin to reset your two-factor authentication.
                </p>
                <Button type='link' block onClick={() => restart()} style={twoFa.link}>
                  Use a different account
                </Button>
              </div>
            )}

            <p style={{ textAlign: 'center', color: '#bfbfbf', fontSize: 12, marginTop: 8 }}>
              <LockOutlined style={{ marginRight: 5 }} />
              Secured with enterprise-grade encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const card = {
  wrapper: {
    display: 'flex',
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    background: '#fff',
  },
  brand: {
    flex: '0 0 42%',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  brandBg: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(160deg, #1a3a22 0%, #122117 100%)',
    zIndex: 0,
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
    padding: '40px 32px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  logo: {
    height: 36,
    width: 'auto',
    marginBottom: 32,
    objectFit: 'contain',
  },
  brandTitle: {
    color: '#ffffff',
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: 12,
  },
  brandSub: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 13,
    lineHeight: 1.65,
    marginBottom: 28,
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 13,
  },
  featureDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#3daa4e',
    flexShrink: 0,
  },
  form: {
    flex: 1,
    // minWidth 0 and 36px sides keep the six 2FA pin boxes beside the brand
    // panel; at 40px they need 396px and the card wraps onto two rows.
    minWidth: 0,
    padding: '40px 36px 32px',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    // Override the base 60% width from login.less
    width: 'auto !important',
  },
};

const twoFa = {
  label: { color: '#595959', fontSize: 13, lineHeight: 1.6, margin: '0 0 10px' },
  qr: { display: 'flex', justifyContent: 'center', margin: '0 0 18px' },
  qrImg: { width: 176, height: 176, border: '1px solid #f0f0f0', borderRadius: 12, padding: 8 },
  qrPlaceholder: { width: 176, height: 176, borderRadius: 12, background: '#f5f5f5' },
  key: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: '8px 10px',
    margin: '0 0 22px',
  },
  keyCode: {
    flex: 1,
    color: '#122117',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 13,
    letterSpacing: 1,
    wordBreak: 'break-all',
    background: 'none',
  },
  help: { textAlign: 'center', color: '#8c8c8c', fontSize: 12, lineHeight: 1.6, margin: '14px 0 0' },
  link: { color: '#8c8c8c', fontSize: 13, marginTop: 8 },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'rgba(74,183,89,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
};

export default SignIn;
